/**
 * `GET people/{person}/exposures/stats[?months=3|6|9]`: the one read of a member's exposure statistics, computed once
 * per request. `months` is the Digital Footprint window: one of `EXPOSURE_STATS_WINDOWS`, the default when absent or
 * empty, and any other value is refused with a 422 in the v3 error envelope.
 *
 * The answer carries exactly the fields some client of the route reads, and no others: the risk change the app's
 * dashboard states, the removal figures its Family tab states, and the Digital Footprint figures both dashboards state.
 * A client reads only the part it states; this card reads `digital_footprint`.
 *
 * The contract's source of truth is app-api's `PersonExposureStatsV3Resource`.
 */
export interface ExposureStats {
    /** The member's privacy risk: the score now, and the baseline of any change in it the member is shown. */
    forecast: {
        /** The risk score now, an integer. */
        risk: number;
        /**
         * The member's previous risk score, an integer: the latest recorded on or before 30 days back, and null when none
         * was. It is the one baseline of any risk change the member is shown, `risk` less `previous`; a client never
         * derives a change from a history, and with no previous score it states no change.
         */
        previous: number | null;
    };
    /** Over the member's whole history. */
    all_time: {
        /** Every closed row: `meta.removal_activity.completed`. */
        removals_completed: number;
    };
    /** What stands now. */
    current: {
        /** Rows in progress: `meta.removal_activity.in_progress`. */
        removals_in_progress: number;
    };
    /** What moved over the window. */
    digital_footprint: DigitalFootprintFigures;
}

/** The Digital Footprint windows the stats read offers, in months. Any other `months` is a 422. */
export const EXPOSURE_STATS_WINDOWS = [3, 6, 9] as const;

export type ExposureStatsWindow = (typeof EXPOSURE_STATS_WINDOWS)[number];

/** The window the stats read answers when `months` names none. */
export const DEFAULT_EXPOSURE_STATS_WINDOW: ExposureStatsWindow = 6;

/** The four Digital Footprint figures, in the order the card states them. */
export const DIGITAL_FOOTPRINT_FIGURES = ['scans_performed', 'exposures_found', 'removals_requested', 'removals_completed'] as const;

/**
 * The Digital Footprint figures: what moved over the member's last `months` calendar months, today included. A removal
 * counts in the window its status was reached in, so a window holding every status change the member has states the
 * same `requested` and `completed` as `meta.removal_activity`.
 */
export type DigitalFootprintFigures = Record<(typeof DIGITAL_FOOTPRINT_FIGURES)[number], number>;
